import React from 'react';

interface MembershipCardProps {
  member: {
    firstName: string;
    lastName: string;
    memberNumber: string;
    membershipType: string;
    status: string;
    profilePhotoS3Key?: string;
    email: string;
    membershipYear?: number;
    membershipExpiryDate?: string;
    renewalStatus?: string;
    isCommittee?: boolean;
  };
  profilePhotoUrl?: string;
}

const formatMembershipType = (type: string) =>
  type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const getStatusColor = (status: string) => {
  switch (status) {
    case 'VERIFIED': return 'bg-green-100 text-green-800';
    case 'PENDING':  return 'bg-yellow-100 text-yellow-800';
    case 'REJECTED': return 'bg-red-100 text-red-800';
    default:         return 'bg-gray-100 text-gray-800';
  }
};

// Per-type accent strip (Tailwind gradients)
const accentFor = (type: string): string => {
  switch (type) {
    case 'FAMILY':         return 'from-orange-300 via-amber-400 to-orange-300';
    case 'JUNIOR':         return 'from-green-300 via-emerald-400 to-green-300';
    case 'SPONSOR_GOLD':   return 'from-yellow-300 via-yellow-500 to-yellow-300';
    case 'SPONSOR_SILVER': return 'from-gray-200 via-gray-400 to-gray-200';
    case 'SPONSOR_BRONZE': return 'from-amber-400 via-amber-700 to-amber-400';
    case 'ADULT':
    case 'SINGLE':
    default:               return 'from-sky-300 via-blue-400 to-sky-300';
  }
};

// Optional visual suffix for sponsor tiers
const typeDisplay = (type: string): string => {
  switch (type) {
    case 'SPONSOR_GOLD':   return '🥇 Gold Sponsor';
    case 'SPONSOR_SILVER': return '🥈 Silver Sponsor';
    case 'SPONSOR_BRONZE': return '🥉 Bronze Sponsor';
    case 'FAMILY':         return '👨‍👩‍👧 Family';
    case 'JUNIOR':         return '🐟 Junior';
    case 'ADULT':
    case 'SINGLE':
    default:               return '🎣 Adult';
  }
};

const MembershipCard: React.FC<MembershipCardProps> = ({ member, profilePhotoUrl }) => {
  const isNotCurrent = member.renewalStatus === 'EXPIRED' || member.renewalStatus === 'LAPSED';

  return (
    <div className="flex justify-center">
      <div
        className="relative rounded-2xl shadow-2xl p-5 sm:p-6 overflow-hidden w-full"
        style={{
          backgroundImage: 'url(/tumbulgum-river.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 60%',
          maxWidth: '480px',
          minHeight: '200px',
          aspectRatio: '1.586 / 1',
        }}
      >
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/35 to-black/70 rounded-2xl" />

        {/* Accent strip across the top — themed per membership type */}
        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accentFor(member.membershipType)} rounded-t-2xl`} />

        {/* Committee Member ribbon (top-left, doesn't clash with right-side photo) */}
        {member.isCommittee && (
          <div className="absolute top-3 left-3 z-20 bg-purple-600/90 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full shadow-lg tracking-wide uppercase">
            ⭐ Committee
          </div>
        )}

        {/* NOT CURRENT stamp */}
        {isNotCurrent && (
          <div className="absolute inset-0 bg-red-900/40 z-10 flex items-center justify-center rounded-2xl">
            <span className="text-white text-2xl sm:text-3xl font-extrabold tracking-widest uppercase rotate-[-15deg] border-4 border-white/70 px-6 py-2 rounded-lg bg-red-600/60">
              Not Current
            </span>
          </div>
        )}

        <div className="relative z-10 flex gap-4 h-full text-white">
          {/* Left column — header + details */}
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-12 w-auto drop-shadow-lg" />
              <div>
                <h3 className="font-bold text-lg leading-tight tracking-wide">Tumbulgum</h3>
                <p className="text-xs opacity-90 uppercase tracking-widest">Fishing Club</p>
              </div>
            </div>

            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold leading-tight truncate">
                {member.firstName} {member.lastName}
              </h2>
              <p className="text-sm opacity-90">{typeDisplay(member.membershipType) || formatMembershipType(member.membershipType)}</p>
              <p className="text-xl sm:text-2xl font-mono font-semibold tracking-widest mt-1 drop-shadow">
                {member.memberNumber || 'Pending'}
              </p>
              {!member.isCommittee && (
                <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusColor(member.status)}`}>
                  {member.status}
                </span>
              )}
            </div>

            {member.membershipYear && (
              <div className="pt-3 mt-3 border-t border-white/20 text-xs opacity-90 flex items-center justify-between">
                <span>Member since {member.membershipYear}</span>
                <span>Valid until 31 Dec {member.membershipYear}</span>
              </div>
            )}
          </div>

          {/* Right column — portrait photo */}
          <div className="flex-shrink-0">
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt={`${member.firstName} ${member.lastName}`}
                className="w-20 h-28 sm:w-24 sm:h-32 rounded-lg border-2 border-white/60 object-cover shadow-lg"
              />
            ) : (
              <div className="w-20 h-28 sm:w-24 sm:h-32 rounded-lg border-2 border-white/60 bg-white/15 flex items-center justify-center shadow-lg">
                <span className="text-4xl">🎣</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCard;
